import type {
  CheckDefinition,
  CheckRule,
  CheckRuleCondition,
} from '@internal/backstage-plugin-pulse-common';
import type { PulseIntegrationItem } from '../../hooks';

/** All distinct fact refs referenced anywhere in a check's rule. */
export const getCheckFactRefs = (rule: CheckRule): string[] => {
  const refs: string[] = [];
  const walk = (node: CheckRule | CheckRuleCondition) => {
    if ('factRef' in node) {
      refs.push(node.factRef);
      return;
    }
    [...(node.all ?? []), ...(node.any ?? [])].forEach(walk);
  };
  walk(rule);
  return [...new Set(refs)];
};

/** Turns a fact ref (`<integrationId>:default/<factName>`) into a concise,
 * human-readable schedule for the collector that produces it. */
const describeCron = (cron: string): string => {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) {
    return `cron(${cron})`;
  }
  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;
  const isNumber = (value: string) => /^\d+$/.test(value);
  const everyPattern = /^\*\/(\d+)$/;

  if (
    isNumber(minute) &&
    isNumber(hour) &&
    dayOfMonth === '*' &&
    month === '*' &&
    dayOfWeek === '*'
  ) {
    return `daily at ${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
  }
  if (
    minute === '0' &&
    everyPattern.test(hour) &&
    dayOfMonth === '*' &&
    month === '*' &&
    dayOfWeek === '*'
  ) {
    const hours = everyPattern.exec(hour)?.[1] ?? '';
    return `every ${hours} hour${hours === '1' ? '' : 's'}`;
  }
  if (
    everyPattern.test(minute) &&
    hour === '*' &&
    dayOfMonth === '*' &&
    month === '*' &&
    dayOfWeek === '*'
  ) {
    const minutes = everyPattern.exec(minute)?.[1] ?? '';
    return `every ${minutes} minute${minutes === '1' ? '' : 's'}`;
  }
  return `cron(${cron})`;
};

const findCollectorCron = (
  factRef: string,
  integrations: PulseIntegrationItem[],
): string | undefined => {
  const [integrationId] = factRef.split(':');
  const factName = factRef.split('/').pop();
  const collector = integrations
    .find(integration => integration.integrationId === integrationId)
    ?.factCollectors.find(
      factCollector => factCollector.factCollectorId === factName,
    );
  return collector?.schedule.frequency?.cron;
};

/** Describes when a check will next be evaluated, based on the schedules of the
 * fact collectors it references. Checks run as part of fact collection, so this
 * is the collectors' cadence rather than a per-check schedule. Undefined when no
 * referenced collector declares a schedule. */
export const describeCheckRunSchedule = (
  check: CheckDefinition,
  integrations: PulseIntegrationItem[],
): string | undefined => {
  const crons = new Set<string>();
  for (const factRef of getCheckFactRefs(check.rule)) {
    const cron = findCollectorCron(factRef, integrations);
    if (cron) {
      crons.add(cron);
    }
  }
  if (crons.size === 0) {
    return undefined;
  }
  return [...crons].map(describeCron).join(' and ');
};

/** Header entry describing a check's cadence. Manual checks are never
 * evaluated, so they report how they change rather than when they run. */
export const describeCheckCadence = (
  check: CheckDefinition,
  integrations: PulseIntegrationItem[],
): { label: string; value: string } =>
  check.type === 'manual'
    ? { label: 'Updates', value: 'manually' }
    : {
        label: 'Runs',
        value: describeCheckRunSchedule(check, integrations) ?? 'unknown',
      };

/** Copy for the success banner shown after a check is created or updated.
 * Only promises a run when the check is automatic and its cadence is known. */
export const describeCheckSavedNotice = (
  check: CheckDefinition,
  integrations: PulseIntegrationItem[],
  notice: 'created' | 'updated',
): string => {
  const isManual = check.type === 'manual';
  if (notice === 'updated') {
    return isManual
      ? 'Check updated.'
      : 'Check updated. Changes will be reflected in the next run.';
  }
  if (isManual) {
    return 'Check created. Set its result on each entity\u2019s Pulse tab.';
  }
  const runSchedule = describeCheckRunSchedule(check, integrations);
  return runSchedule
    ? `Check created. It runs ${runSchedule}.`
    : 'Check created.';
};
